import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { plainToInstance } from 'class-transformer';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { SiloBag } from '../models/silo-bag.model';

/**
 * Lista las silobolsas de una empresa a través del PROXY del backend
 * (`GET /:apiBase/companies/:companyId/siloreal/silobags`).
 *
 * El backend resuelve la autenticación con Siloreal usando las credenciales de la
 * empresa (tabla `siloreal_credentials`) y ya excluye del listado las silobolsas
 * comprometidas por órdenes/negociaciones activas. El front NO tiene credenciales de
 * Siloreal ni llama a Siloreal directamente.
 */
@Injectable({
  providedIn: 'root'
})
export class SiloBagService {

  private readonly url = '/:apiBase/companies/:companyId/siloreal/silobags';

  constructor(
    private http: HttpClient
  ) { }

  /**
   * @param companyId            Empresa dueña de las silobolsas.
   * @param excludeNegotiationId En edición de contraorden, deja visibles las bolsas ya
   *                             elegidas en esa misma negociación (no las auto-oculta).
   */
  public listByCompany(companyId: number, excludeNegotiationId?: number): Observable<SiloBag[]> {
    const url = this.url.replace(':companyId', companyId.toString());

    let params = new HttpParams();
    if (excludeNegotiationId) {
      params = params.set('exclude_negotiation_id', excludeNegotiationId.toString());
    }

    return this.http.get<{ data?: any[] }>(url, { params }).pipe(
      map(response => (response?.data ?? []).map(item => this.mapToSiloBag(item)))
    );
  }

  /**
   * Mapea un item normalizado del proxy backend al modelo SiloBag.
   * El backend (SilorealSilobagController::normalizeSilobag) ya aplanó el item de
   * Siloreal a este shape estable, así que el front no conoce la estructura de Siloreal.
   */
  private mapToSiloBag(item: any): SiloBag {
    const baggedDate = this.toDateString(item.bagged_date ?? null);

    return plainToInstance(SiloBag, {
      silo_id:            String(item.silobag_id ?? ''),
      silobag_uuid:       item.silobag_uuid ?? null,
      silobag_name:       item.silobag_name ?? null,
      grain_type:         (item.species ?? '').toString().toLowerCase(),
      lot_name:           item.lot_name ?? null,
      countryside_name:   item.countryside_name ?? null,
      // Siloreal expresa stock en kg; la tabla muestra toneladas.
      bagged_weight:      this.toTons(item.remaining_stock ?? item.input_weight ?? null),
      remaining_stock:    this.toFiniteNumber(item.remaining_stock ?? null),
      input_weight:       this.toFiniteNumber(item.input_weight ?? null),
      humidity:           this.toFiniteNumber(item.humidity ?? null),
      bagged_date:        baggedDate,
      days_since_bagging: baggedDate ? this.daysSince(baggedDate) : 0,
      latitude:           this.toFiniteNumber(item.latitude ?? null),
      longitude:          this.toFiniteNumber(item.longitude ?? null),
      harvest:            item.harvest ?? null,
      organization:       item.organization ?? null,
      // El backend ya filtra las comprometidas; lo que llega está disponible.
      assigned_order_id:  null,
    });
  }

  private toTons(raw: any): number {
    const parsed = this.toFiniteNumber(raw);
    return parsed != null ? parsed / 1000 : 0;
  }

  private toFiniteNumber(raw: any): number | null {
    if (raw == null || raw === '') return null;
    const parsed = parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private toDateString(raw: string | null): string | null {
    if (!raw) return null;
    return raw.slice(0, 10);
  }

  private daysSince(dateStr: string): number {
    const [year, month, day] = dateStr.split('-').map(Number);
    if (!year || !month || !day) return 0;
    const ms = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(ms / 86_400_000);
  }
}
